import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

// English is the ONLY locale bundled statically. The other five are pulled in
// with dynamic import() below, so webpack emits them as separate chunks.
//
// This file used to `import` all six default.json files at the top level:
// 2.00 MB of raw JSON (~500 KB gzipped) landing in main.js, of which five
// sixths were dead weight for any given merchant. That was well over half the
// entire main bundle and the single biggest thing standing between this app and
// the Built for Shopify LCP budget. Never add a static locale import back here.
import en from './en/default.json';

const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'pt', 'de', 'it'];

// One chunk per locale. The explicit switch (rather than a template literal in
// the import path) is deliberate — it keeps webpack from having to emit a
// context module that can reach every JSON file under ./, which is how you
// accidentally end up shipping all six again.
const loadLanguage = (lng) => {
  switch (lng) {
    case 'de':
      return import(/* webpackChunkName: "locale-de" */ './de/default.json');
    case 'es':
      return import(/* webpackChunkName: "locale-es" */ './es/default.json');
    case 'fr':
      return import(/* webpackChunkName: "locale-fr" */ './fr/default.json');
    case 'it':
      return import(/* webpackChunkName: "locale-it" */ './it/default.json');
    case 'pt':
      return import(/* webpackChunkName: "locale-pt" */ './pt/default.json');
    default:
      return null;
  }
};

const normalizeLanguage = (value) => {
  if (!value) return null;
  const normalized = value.toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(normalized) ? normalized : null;
};

const queryParams = new URLSearchParams(window.location?.search);
const shopifyLocale = queryParams.get('locale');
const savedAppLanguage = localStorage.getItem('app_language');

const hasUserOverride = savedAppLanguage && savedAppLanguage.trim() !== '';

const initialLanguage = hasUserOverride
  ? normalizeLanguage(savedAppLanguage) || 'en'
  : normalizeLanguage(shopifyLocale) || 'en';

i18next.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: initialLanguage,
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  // Without this, i18next treats a language with no resource bundle as having
  // no keys at all and t() returns the raw key. We add the bundle immediately
  // after (see ensureLanguage) — this just lets it fall back to `en` in the
  // window before that resolves instead of rendering key names.
  partialBundledLanguages: true,
  interpolation: {
    escapeValue: false,
  },
});

const loaded = new Set(['en']);

// Fetches a locale chunk (once) and registers it. Safe to call for 'en' or an
// unsupported value — both resolve immediately without a network request.
export async function ensureLanguage(lng) {
  const normalized = normalizeLanguage(lng);
  if (!normalized || loaded.has(normalized)) return;

  const request = loadLanguage(normalized);
  if (!request) return;

  try {
    const mod = await request;
    i18next.addResourceBundle(normalized, 'translation', mod.default || mod, true, true);
    loaded.add(normalized);
  } catch (error) {
    // A failed chunk fetch must not take the app down — English is already
    // registered as fallbackLng, so the UI stays readable.
    console.error(`[i18n] failed to load locale "${normalized}":`, error);
  }
}

// Awaited by src/index.js BEFORE the first render. For the common case
// (English) this resolves on the same tick with no request at all, so it costs
// nothing; for the other five it's one small chunk. Doing it here rather than
// letting the app paint English first and swap is what keeps a non-English
// merchant from getting a full-page text swap — which would be a CLS hit, not
// just a cosmetic flash.
export const i18nReady = ensureLanguage(initialLanguage);

// Loads the target locale before switching, so t() never renders a window of
// raw keys. Use this instead of calling i18next.changeLanguage() directly.
export async function changeLanguage(lng) {
  await ensureLanguage(lng);
  return i18next.changeLanguage(lng);
}

export default i18next;
